import { LoginPropsCommon } from "./typings";
import { Trans, useLingui } from "@lingui/react/macro";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import metrics from "~/utils/metrics";
import React from "react";
import { classNames } from "~/utils";
import { useSearchParams } from "react-router";
import * as jose from "jose";
import { Button, Input } from "@mindstonehq/ui";
import { LanguageBanner } from "~/components/authentication/language-banner";

interface SignupProps extends LoginPropsCommon {
  success: boolean;
  error: boolean;
  redirectUrl: string;
}

export function SignupEmail({ error, redirectUrl, routerUtils, appRoutes }: SignupProps) {
  const { Link } = routerUtils;
  const { t, i18n } = useLingui();
  const [language, setLanguage] = React.useState(i18n.locale);
  const [submitting, setSubmitting] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);

  const [searchParams] = useSearchParams();
  const isOnlineEventRedirect = searchParams.get("onlineEventRedirect") === "true";
  const isCommunityEvent = searchParams.get("eventFlow") === "community";
  searchParams.delete("error");

  const inviteCode = React.useMemo(() => searchParams.get("inviteCode") || "", [searchParams]);

  const linkSearchParams = React.useMemo(() => {
    const sp = new URLSearchParams(searchParams);
    if (email) sp.set("email", email);
    else sp.delete("email");
    return sp.toString();
  }, [searchParams, email]);

  React.useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam && !email) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  React.useEffect(() => {
    if (!inviteCode) return;
    try {
      const claims = jose.decodeJwt(inviteCode);
      const metadata = claims.Metadata
        ? JSON.parse(new TextDecoder().decode(jose.base64url.decode(claims.Metadata as string)))
        : null;
      if (metadata?.email) {
        setEmail(metadata.email);
        setInviteEmail(metadata.email);
      }
    } catch (error) {
      console.error("Failed to decode email from invite code:", error);
    }
  }, [inviteCode]);

  React.useEffect(() => {
    if (error) {
      setPassword("");
      setSubmitting(false);
    }
  }, [error]);

  const invalidInviteEmail = React.useMemo(
    () => inviteCode && inviteEmail !== "" && email.toLocaleLowerCase() !== inviteEmail.toLowerCase(),
    [inviteCode, inviteEmail, email],
  );

  return (
    <div className="flex flex-col grow justify-center items-center w-full max-w-[400px]">
      <form
        method="POST"
        action="/signup/email"
        target="_self"
        className="w-full flex flex-col"
        onSubmit={() => {
          setSubmitting(true);
          metrics.track("authentication_start", { provider: "mindstone" });
        }}
      >
        <input type="hidden" name="state" value={invalidInviteEmail ? "" : redirectUrl} />
        <input type="hidden" name="inviteCode" value={inviteCode} />
        <input type="hidden" name="onlineEventRedirect" value={isOnlineEventRedirect ? "true" : ""} />
        <input type="hidden" name="meetupIntent" value={searchParams.get("meetupIntent") ?? ""} />
        <input type="hidden" name="eventFlow" value={searchParams.get("eventFlow") ?? ""} />

        {/* Form Fields Section */}
        <div className="flex flex-col gap-2 w-full">
          {/* Language banner — shown for invited cohort signups; pre-populated
              from the browser-detected locale, live-switches the UI, and writes
              a locale cookie so the pick carries into onboarding. */}
          {inviteCode && <LanguageBanner value={language} onChange={setLanguage} />}

          {/* Email Input */}
          <div className="flex flex-col gap-1.5 w-full">
            <label htmlFor="email" className="text-sm font-semibold leading-5 text-foreground">
              <Trans>Email</Trans>
            </label>
            <Input
              type="email"
              name="email"
              id="email"
              placeholder={t`Type your email`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.code === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              ref={emailRef}
              className={classNames("h-10 placeholder:text-placeholder-foreground", error ? "border-destructive" : "")}
            />
          </div>

          {invalidInviteEmail && (
            <div className="text-sm text-destructive">
              <Trans>
                The email does not match the invited email. The invite will therefore not be accepted.{" "}
                <a href="mailto:support@mindstone.com" className="text-info hover:text-info-hover cursor-pointer">
                  Contact support
                </a>
              </Trans>
            </div>
          )}

          {/* Password Input */}
          <div className="flex flex-col gap-1.5 w-full">
            <label htmlFor="password" className="text-sm font-semibold leading-5 text-foreground">
              <Trans>Password</Trans>
            </label>
            <div className="relative">
              <Input
                type={passwordVisible ? "text" : "password"}
                name="password"
                id="password"
                placeholder={t`Create a password`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                ref={passwordRef}
                className="h-10 pr-10 placeholder:text-placeholder-foreground"
              />
              <button
                type="button"
                onClick={() => setPasswordVisible(!passwordVisible)}
                aria-label={passwordVisible ? t`Hide password` : t`Show password`}
                aria-pressed={passwordVisible}
                aria-controls="password"
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {passwordVisible ? (
                  <EyeIcon className="w-5 h-5 text-placeholder-foreground" />
                ) : (
                  <EyeSlashIcon className="w-5 h-5 text-placeholder-foreground" />
                )}
              </button>
            </div>
            <p className="text-xs leading-5 text-placeholder-foreground"><Trans>Must be at least 8 characters</Trans></p>
          </div>
        </div>

        {isOnlineEventRedirect && isCommunityEvent && (
          <div className="text-xs text-left text-muted-foreground mt-2">
            <Trans>You'll be checked in to the event as soon as you sign up.</Trans>
          </div>
        )}

        {isOnlineEventRedirect && !isCommunityEvent && (
          <div className="text-xs text-left text-warning-hover mt-2 flex flex-col gap-px">
            <Trans>To join the live session, please ensure:</Trans>
            <div className="pl-1"><Trans>1. You've received an invitation to the program</Trans></div>
            <div className="pl-1"><Trans>2. Your signup email is the same as the one that you were invited with</Trans></div>
            <div>
              <Trans>
                Not received an invite? Contact help{" "}
                <a href="mailto:support@mindstone.com" className="text-info hover:text-info-hover cursor-pointer">
                  here
                </a>{" "}
                or reach out to your admin
              </Trans>
            </div>
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive text-left mt-2" id="signup-error">
            <Trans>
              This email is already registered.{" "}
              <Link
                to={{
                  pathname: appRoutes.Login,
                  search: linkSearchParams,
                }}
                className="text-primary-hover hover:underline"
              >
                Sign in
              </Link>{" "}
              instead?
            </Trans>
          </p>
        )}

        {/* Actions Section */}
        <div className="flex flex-col gap-2 w-full mt-6">
          {/* Create Account Button */}
          <Button
            type="submit"
            className="w-full h-10 bg-accent-foreground text-background hover:bg-accent-foreground/90"
            disabled={!password || !email || password.length < 8 || submitting}
            id="create-account"
          >
            <Trans>Create account</Trans>
          </Button>

          {/* Sign In Link */}
          <div className="flex items-center justify-center gap-1 text-xs leading-5">
            <span className="text-theme-text-primary"><Trans>Already have an account?</Trans></span>
            <Link
              to={{
                pathname: appRoutes.Login,
                search: linkSearchParams,
              }}
              className="text-primary-hover hover:underline"
            >
              <Trans>Sign in</Trans>
            </Link>
          </div>
        </div>
      </form>

      {/* Terms and Privacy Policy */}
      <div className="w-full text-center mt-6">
        <p className="text-xs leading-5 text-muted-foreground">
          <Trans>
            By continuing you agree with our{" "}
            <a
              className="text-primary-hover hover:underline"
              target="_blank"
              href="https://mindstone.com/terms-of-service"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a className="text-primary-hover hover:underline" target="_blank" href="https://mindstone.com/privacy-policy">
              Privacy Policy
            </a>
          </Trans>
        </p>
      </div>
    </div>
  );
}

export default SignupEmail;
